import { useMemo } from 'react';
import type { V2TenantGroupListResponse } from '@/api/white-labels';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchSelect } from '@/components/ui/search-select';
import { Globe, Home, Plus, Server } from 'lucide-react';

/**
 * Choix du tenant group cible d'un déploiement V2 :
 * - default : groupe École Futée (interdit pour une école en marque blanche)
 * - existing : réseau physique existant
 * - new : création d'un réseau physique (nom + host) au moment du déploiement
 */
export interface PhysicalNetworkChoice {
  mode: 'default' | 'existing' | 'new';
  /** mode existing : id du réseau physique local */
  networkId: number | null;
  newNetworkName: string;
  newNetworkHost: string;
  /** modes existing & new : host propre de l'école (défaut = host du réseau) */
  schoolHost: string;
}

export function defaultPhysicalNetworkChoice(requiresNetwork: boolean): PhysicalNetworkChoice {
  return {
    mode: requiresNetwork ? 'existing' : 'default',
    networkId: null,
    newNetworkName: '',
    newNetworkHost: '',
    schoolHost: '',
  };
}

interface PhysicalNetworkSelectorProps {
  value: PhysicalNetworkChoice;
  onChange: (value: PhysicalNetworkChoice) => void;
  groups: V2TenantGroupListResponse['data'];
  /** École en marque blanche : le groupe par défaut est interdit */
  requiresNetwork: boolean;
}

export function PhysicalNetworkSelector({
  value,
  onChange,
  groups,
  requiresNetwork,
}: PhysicalNetworkSelectorProps) {
  const networkGroups = useMemo(() => groups.filter((g) => !g.isDefault && g.networkId), [groups]);

  const networkOptions = useMemo(
    () =>
      networkGroups.map((g) => ({
        value: String(g.networkId),
        label: `${g.name} (${g.host})`,
        searchText: `${g.name} ${g.host}`,
      })),
    [networkGroups]
  );

  const selectedNetwork = networkGroups.find((g) => g.networkId === value.networkId) || null;

  const setMode = (mode: PhysicalNetworkChoice['mode']) => {
    onChange({ ...value, mode });
  };

  const optionClass = (active: boolean, disabled = false) =>
    `w-full rounded-lg border-2 p-3 text-left transition-colors ${
      disabled
        ? 'border-border opacity-50 cursor-not-allowed'
        : active
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-muted-foreground/40'
    }`;

  return (
    <div className="space-y-3">
      <Label>Tenant group cible</Label>

      <div className="space-y-2">
        {/* Groupe par défaut */}
        <button
          type="button"
          disabled={requiresNetwork}
          onClick={() => setMode('default')}
          className={optionClass(value.mode === 'default', requiresNetwork)}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <Home className="h-4 w-4" />
            Groupe École Futée (par défaut)
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {requiresNetwork
              ? 'Indisponible : une école en marque blanche doit appartenir à un réseau physique.'
              : 'L\'école rejoint le tenant group mutualisé École Futée.'}
          </p>
        </button>

        {/* Réseau existant */}
        <button
          type="button"
          onClick={() => setMode('existing')}
          className={optionClass(value.mode === 'existing')}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <Server className="h-4 w-4" />
            Rattacher à un réseau physique existant
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            L'école rejoint définitivement le tenant group du réseau choisi.
          </p>
        </button>

        {value.mode === 'existing' && (
          <div className="ml-4 space-y-2 rounded-lg border bg-muted/30 p-3">
            <SearchSelect
              options={networkOptions}
              value={value.networkId ? String(value.networkId) : ''}
              onValueChange={(v) => {
                const group = networkGroups.find((g) => String(g.networkId) === v) || null;
                onChange({
                  ...value,
                  networkId: v ? Number(v) : null,
                  schoolHost: value.schoolHost || group?.host || '',
                });
              }}
              placeholder="Sélectionner un réseau physique..."
              searchPlaceholder="Rechercher un réseau..."
            />
            {networkOptions.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Aucun réseau physique existant : créez-en un nouveau ci-dessous.
              </p>
            )}
          </div>
        )}

        {/* Nouveau réseau */}
        <button
          type="button"
          onClick={() => setMode('new')}
          className={optionClass(value.mode === 'new')}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <Plus className="h-4 w-4" />
            Créer un nouveau réseau physique
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Crée le tenant group V2 et sa marque blanche, puis y rattache l'école.
          </p>
        </button>

        {value.mode === 'new' && (
          <div className="ml-4 space-y-3 rounded-lg border bg-muted/30 p-3">
            <div className="space-y-1">
              <Label htmlFor="pn-new-name" className="text-xs">
                Nom du réseau
              </Label>
              <Input
                id="pn-new-name"
                value={value.newNetworkName}
                onChange={(e) => onChange({ ...value, newNetworkName: e.target.value })}
                placeholder="Ex: Réseau Montessori 21"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pn-new-host" className="text-xs flex items-center gap-1">
                <Globe className="h-3 w-3" />
                Domaine (host) du réseau
              </Label>
              <Input
                id="pn-new-host"
                value={value.newNetworkHost}
                onChange={(e) =>
                  onChange({
                    ...value,
                    newNetworkHost: e.target.value,
                    schoolHost: value.schoolHost || e.target.value,
                  })
                }
                placeholder="mon-reseau.ecole-futee.com"
                className="font-mono"
              />
            </div>
          </div>
        )}
      </div>

      {/* Host de l'école */}
      {value.mode !== 'default' && (
        <div className="space-y-1">
          <Label htmlFor="pn-school-host" className="text-xs flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Domaine (host) de l'école
          </Label>
          <Input
            id="pn-school-host"
            value={value.schoolHost}
            onChange={(e) => onChange({ ...value, schoolHost: e.target.value })}
            placeholder={
              value.mode === 'existing'
                ? selectedNetwork?.host || 'ecole.mon-reseau.fr'
                : value.newNetworkHost || 'ecole.mon-reseau.fr'
            }
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Prérempli avec le domaine du réseau ; modifiable si l'école a son propre domaine.
          </p>
        </div>
      )}
    </div>
  );
}
